























































































































































import { getUserData } from "@/auth/getUserData";
import ToastificationContent from "@/layouts/components/toastification/toastificationContent.vue";
import i18n from "@/libs/i18n";
import { useChannelsStore } from "@/store/useChannelsStore";
import { useChatsStore } from "@/store/useChatsStore";
import { useContactsStore } from "@/store/useContactsStore";
import { useUsersStore } from "@/store/useUsersStore";
import { formatDate } from "@/utils/luxon";
import { InternalComment } from "@saysimple-platform/dtos";
import { computed, ref } from "@vue/composition-api";
import { defineComponent } from "@vue/composition-api/dist/vue-composition-api";
import {
    BAlert,
    BAvatar,
    BButton,
    BCard,
    BCardBody,
    BCardHeader,
    BCardText,
    BCardTitle,
    BDropdown,
    BDropdownItem,
    BForm,
    BFormTextarea,
    BOverlay,
    BSpinner,
} from "bootstrap-vue";
import lodash from "lodash";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { useToast } from "vue-toastification/composition";

type InternalCommentsList = Array<InternalComment | { type: "event"; date: Date }>;

export default defineComponent({
    components: {
        BAlert,
        BAvatar,
        BButton,
        BCard,
        BCardBody,
        BCardHeader,
        BCardText,
        BCardTitle,
        BDropdown,
        BDropdownItem,
        BForm,
        BFormTextarea,
        BOverlay,
        BSpinner,
        ValidationObserver,
        ValidationProvider,
    },
    setup(props, ctx) {
        const contactsStore = useContactsStore();
        const channelsStore = useChannelsStore();
        const toast = useToast();

        const internalCommentId = ref();
        const internalCommentForm = ref({
            comment: "",
            createdAt: new Date(),
        });

        const prefill = (comment: InternalComment): void => {
            internalCommentId.value = comment.id;
            internalCommentForm.value.comment = comment.comment;
            internalCommentForm.value.createdAt = new Date(comment.createdAt);
        };

        const reset = (): void => {
            internalCommentId.value = null;
            internalCommentForm.value = {
                comment: "",
                createdAt: new Date(),
            };
        };

        const internalCommentsLog = computed((): InternalCommentsList => {
            if (!contactsStore.activeChatContact) {
                return;
            }

            const internalCommentsList: InternalCommentsList = [];
            let currentDate = new Date().toString();
            let currentUserId = 0;

            lodash.forEach(contactsStore.activeChatContact.internalComments, (internalComment: InternalComment) => {
                if (currentUserId === 0) {
                    currentUserId = internalComment.userId;
                }

                let commentDate: Date | string = new Date(internalComment.createdAt);
                commentDate = (
                    commentDate.getFullYear() +
                    "" +
                    (commentDate.getMonth() + 1) +
                    "" +
                    commentDate.getDate()
                ).substring(0, 10);

                if (commentDate !== currentDate) {
                    currentDate = commentDate;

                    internalCommentsList.push({
                        type: "event",
                        date: internalComment.createdAt,
                    });
                }

                // If we 'switch' users in the comments, we add an extra value for design purposes
                if (currentUserId !== internalComment.userId) {
                    // @ts-ignore
                    internalComment.otherUser = true;
                    currentUserId = internalComment.userId;
                }

                internalCommentsList.push(internalComment);
            });

            return internalCommentsList;
        });

        const getUserName = (comment: InternalComment): string => {
            const { users } = useUsersStore();

            const user = users.find((user) => user.id === comment.userId);

            if (user) {
                return user.name;
            }
        };

        // For new comments we have to use another object because both options, new and edit, are possible at the same time
        const internalCommentFormNew = ref({
            comment: "",
        });

        const createInternalComment = (): void => {
            const chatsStore = useChatsStore();
            if (!chatsStore.activeChat || !contactsStore.activeChatContact) {
                return;
            }

            contactsStore
                .createInternalComment({
                    comment: internalCommentFormNew.value.comment,
                    userId: getUserData().id,
                    contactId: contactsStore.activeChatContact.id,
                    chatId: chatsStore.activeChat.chat.id,
                    channelId: channelsStore.activeChatChannel?.id,
                })
                .then(() => {
                    internalCommentFormNew.value = {
                        comment: "",
                    };

                    toast({
                        component: ToastificationContent,
                        props: {
                            title: i18n.t("entities.internal-comments.Internal comment"),
                            text: i18n.t("toast.create.success", {
                                entity: i18n.t("entities.internal-comments.Internal comment"),
                            }),
                            icon: "check",
                            variant: "success",
                        },
                    });
                })
                .catch(() => {
                    toast({
                        component: ToastificationContent,
                        props: {
                            title: i18n.t("entities.internal-comments.Internal comment"),
                            text: i18n.t("toast.create.error", {
                                entity: i18n.t("entities.internal-comments.Internal comment"),
                            }),
                            icon: "times",
                            variant: "danger",
                        },
                    });
                });
        };

        const updateInternalComment = (): void => {
            contactsStore
                .updateInternalComment({
                    id: internalCommentId.value,
                    comment: internalCommentForm.value.comment,
                    chatId: chatsStore.activeChat?.chat.id,
                })
                .then(() => {
                    internalCommentId.value = undefined;

                    toast({
                        component: ToastificationContent,
                        props: {
                            title: i18n.t("entities.internal-comments.Internal comment"),
                            text: i18n.t("toast.update.success", {
                                entity: i18n.t("entities.internal-comments.Internal comment"),
                            }),
                            icon: "check",
                            variant: "success",
                        },
                    });
                })
                .catch(() => {
                    toast({
                        component: ToastificationContent,
                        props: {
                            title: i18n.t("entities.internal-comments.Internal comment"),
                            text: i18n.t("toast.update.error", {
                                entity: i18n.t("entities.internal-comments.Internal comment"),
                            }),
                            icon: "times",
                            variant: "danger",
                        },
                    });
                });
        };

        const deleteInternalComment = (internalCommentId: number): void => {
            ctx.root.$bvModal
                .msgBoxConfirm(i18n.t("entities.internal-comments.confirm-delete.text"), {
                    title: i18n.t("entities.internal-comments.confirm-delete.title"),
                    cancelTitle: i18n.t("confirm.Cancel"),
                    cancelVariant: "light",
                    okTitle: i18n.t("confirm.Yes, delete"),
                    hideHeaderClose: false,
                    headerCloseVariant: "light",
                    centered: true,
                })
                .then((value) => {
                    if (value === true) {
                        contactsStore
                            .deleteInternalComment(internalCommentId)
                            .then(() => {
                                toast({
                                    component: ToastificationContent,
                                    props: {
                                        title: i18n.t("entities.internal-comments.Internal comment"),
                                        text: i18n.t("toast.delete.success", {
                                            entity: i18n.t("entities.internal-comments.Internal comment"),
                                        }),
                                        icon: "check",
                                        variant: "success",
                                    },
                                });
                            })
                            .catch(() => {
                                toast({
                                    component: ToastificationContent,
                                    props: {
                                        title: i18n.t("entities.internal-comments.Internal comment"),
                                        text: i18n.t("toast.delete.error", {
                                            entity: i18n.t("entities.internal-comments.Internal comment"),
                                        }),
                                        icon: "times",
                                        variant: "danger",
                                    },
                                });
                            });
                    }
                });
        };

        const chatsStore = useChatsStore();
        return {
            chatsStore,
            contactsStore,
            createInternalComment,
            deleteInternalComment,
            formatDate,
            getUserData,
            getUserName,
            internalCommentForm,
            internalCommentFormNew,
            internalCommentId,
            internalCommentsLog,
            prefill,
            reset,
            updateInternalComment,
        };
    },
});
